// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import DataUtil from '@/utils/DataUtil';
import ValidationUtil from '@/utils/ValidationUtil';

// Modules
import modules from './modules';

const actionTypes = {
    getCarriers: 'getCarriers',
    getMailTypes: 'getMailTypes',
    getCustomerTypes: 'getCustomerTypes',
    getAllLists: 'getAllLists',
    reset: 'reset',
};
const mutationTypes = {
    SET_CARRIERS_LIST: 'SET_CARRIERS_LIST',
    SET_CARRIERS_IN_FLIGHT: 'SET_CARRIERS_IN_FLIGHT',
    SET_MAIL_TYPES_LIST: 'SET_MAIL_TYPES_LIST',
    SET_MAIL_TYPES_IN_FLIGHT: 'SET_MAIL_TYPES_IN_FLIGHT',
    SET_CUSTOMER_TYPES_LIST: 'SET_CUSTOMER_TYPES_LIST',
    SET_CUSTOMER_TYPES_IN_FLIGHT: 'SET_CUSTOMER_TYPES_IN_FLIGHT',
};

export default {
    namespaced: true,
    modules,
    state: {
        carriers: {
            list: {},
            inFlight: false,
        },
        mailTypes: {
            list: {},
            inFlight: false,
        },
        customerTypes: {
            list: {},
            inFlight: false,
        },
    },
    getters: {
        /** @returns { Array } */
        carriersList: ( state ) => Object.values( state.carriers.list ),
        /** @returns { Boolean } */
        hasCarriers: ( state, getters ) => ( getters.carriersList.length > 0 ),
        /** @returns { Array } */
        mailTypesList: ( state ) => Object.values( state.mailTypes.list ),
        /** @returns { Boolean } */
        hasMailTypes: ( state, getters ) => ( getters.mailTypesList.length > 0 ),
        /** @returns { Array } */
        customerTypesList: ( state ) => Object.values( state.customerTypes.list ),
        /** @returns { Boolean } */
        hasCustomerTypes: ( state, getters ) => ( getters.customerTypesList.length > 0 ),
        /** @returns { Boolean } */
        listIsInFlight: ( state ) => ( state.carriers.inFlight || state.mailTypes.inFlight ),
    },
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.SET_CARRIERS_LIST, { list: [] } );
            commit( mutationTypes.SET_CARRIERS_IN_FLIGHT, { boolean: false } );
            commit( mutationTypes.SET_MAIL_TYPES_LIST, { list: [] } );
            commit( mutationTypes.SET_MAIL_TYPES_IN_FLIGHT, { boolean: false } );
            commit( mutationTypes.SET_CUSTOMER_TYPES_LIST, { list: [] } );
            commit( mutationTypes.SET_CUSTOMER_TYPES_IN_FLIGHT, { boolean: false } );
        },

        async [ actionTypes.getCarriers ]( context ) {
            context.commit( mutationTypes.SET_CARRIERS_IN_FLIGHT, { boolean: true } );
            try {
                const { data } = await LogisticsApi.getMailCarriers( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                context.commit( mutationTypes.SET_CARRIERS_LIST, {
                    list: ValidationUtil.arrayHasItems( data.mailCarriers )
                        ? DataUtil.normalize( data.mailCarriers, 'name' )
                        : {},
                } );
            } catch ( error ) {
                context.commit( mutationTypes.SET_CARRIERS_LIST, { list: {} } );
            } finally {
                context.commit( mutationTypes.SET_CARRIERS_IN_FLIGHT, { boolean: false } );
            }
        },

        async [ actionTypes.getMailTypes ]( context ) {
            context.commit( mutationTypes.SET_MAIL_TYPES_IN_FLIGHT, { boolean: true } );
            try {
                const { data } = await LogisticsApi.getMailTypes( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                if ( ValidationUtil.arrayHasItems( data.mailTypeDTOList ) ) {
                    const transformedList = [ ...data.mailTypeDTOList ];
                    transformedList.unshift( { id: 0, name: 'ALL', friendly: 'All' } );
                    context.commit( mutationTypes.SET_MAIL_TYPES_LIST, {
                        list: DataUtil.normalize( transformedList, 'name' ),
                    } );
                } else {
                    context.commit( mutationTypes.SET_MAIL_TYPES_LIST, { list: {} } );
                }
            } catch ( error ) {
                context.commit( mutationTypes.SET_MAIL_TYPES_LIST, { list: {} } );
            } finally {
                context.commit( mutationTypes.SET_MAIL_TYPES_IN_FLIGHT, { boolean: false } );
            }
        },

        async [ actionTypes.getCustomerTypes ]( context ) {
            context.commit( mutationTypes.SET_CUSTOMER_TYPES_IN_FLIGHT, { boolean: true } );
            try {
                const { data } = await LogisticsApi.getCustomerTypes( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                if ( ValidationUtil.arrayHasItems( data.mailCustomerFilterTypeDTOList ) ) {
                    const customerList = [ ...data.mailCustomerFilterTypeDTOList ];
                    customerList.unshift( { id: 0, name: 'ALL', friendly: 'All' } );
                    context.commit( mutationTypes.SET_CUSTOMER_TYPES_LIST, {
                        list: DataUtil.normalize( customerList, 'name' ),
                    } );
                } else {
                    context.commit( mutationTypes.SET_CUSTOMER_TYPES_LIST, { list: {} } );
                }
            } catch ( error ) {
                context.commit( mutationTypes.SET_CUSTOMER_TYPES_LIST, { list: {} } );
            } finally {
                context.commit( mutationTypes.SET_CUSTOMER_TYPES_IN_FLIGHT, { boolean: false } );
            }
        },

        async [ actionTypes.getAllLists ]( context ) {
            context.dispatch( actionTypes.getCarriers );
            context.dispatch( actionTypes.getMailTypes );
            context.dispatch( actionTypes.getCustomerTypes );
        },
    },
    mutations: {
        [ mutationTypes.SET_CARRIERS_LIST ]( state, { list } ) {
            state.carriers.list = list;
        },
        [ mutationTypes.SET_CARRIERS_IN_FLIGHT ]( state, { boolean } ) {
            state.carriers.inFlight = boolean;
        },
        [ mutationTypes.SET_MAIL_TYPES_LIST ]( state, { list } ) {
            state.mailTypes.list = list;
        },
        [ mutationTypes.SET_MAIL_TYPES_IN_FLIGHT ]( state, { boolean } ) {
            state.mailTypes.inFlight = boolean;
        },
        [ mutationTypes.SET_CUSTOMER_TYPES_LIST ]( state, { list } ) {
            state.customerTypes.list = list;
        },
        [ mutationTypes.SET_CUSTOMER_TYPES_IN_FLIGHT ]( state, { boolean } ) {
            state.customerTypes.inFlight = boolean;
        },
    },
};
